import { useCallback, useMemo } from 'react';

import {
  Checkbox,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Paper,
} from '@mui/material';
import {
  CircleCheckIcon,
  HourglassIcon,
  LockIcon,
  LockOpenIcon,
} from 'lucide-react';

import { FilterChip } from '@modules/_shared/components/filters/quick-filters/FilterChip';
import type { QuickFilterProps } from '@modules/_shared/components/filters/quick-filters/QuickFilters';
import { useFiltersSearchParams } from '@modules/_shared/components/filters/useFiltersSearchParams';
import { useSchema } from '@modules/_shared/components/filters/useSchema';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

type AccessStatus = 'owner' | 'shared' | 'pending' | 'not_shared';

interface AccessOption {
  value: AccessStatus | AccessStatus[];
  label: string;
  icon: React.ReactNode;
  description: string;
}

export const MlsAccessFilter = ({
  label,
  path,
  where,
  addWhereClause,
  deleteWhereClause,
  queryParamsScope,
  disabled,
}: QuickFilterProps) => {
  const { t } = useLocale();
  const [, setFiltersParams] = useFiltersSearchParams(queryParamsScope);
  const { getValueFromPath } = useSchema();

  // Get current filter value from where clause
  const currentValue = useMemo(() => {
    const value = getValueFromPath(path, where)?._in;
    return Array.isArray(value) ? value : [];
  }, [getValueFromPath, path, where]);

  // Define access options
  // Note: "Shared with me" includes both 'owner' and 'shared' statuses
  const accessOptions: AccessOption[] = useMemo(
    () => [
      {
        value: ['owner', 'shared'],
        label: t('Shared with me'),
        icon: (
          <CircleCheckIcon
            size={18}
            color={'var(--mui-palette-success-main)'}
          />
        ),
        description: t('Listings you own or have access to'),
      },
      {
        value: 'not_shared',
        label: t('Not shared'),
        icon: <LockIcon size={18} color={customPalette.gray600} />,
        description: t('Listings you need to request access to'),
      },
      {
        value: 'pending',
        label: t('Pending'),
        icon: (
          <HourglassIcon size={18} color={'var(--mui-palette-warning-main)'} />
        ),
        description: t('Listings with pending access request'),
      },
    ],
    [t],
  );

  // Check if an option is selected
  const isOptionSelected = useCallback(
    (option: AccessOption) => {
      const optionValues = Array.isArray(option.value)
        ? option.value
        : [option.value];
      return optionValues.every(v => currentValue.includes(v));
    },
    [currentValue],
  );

  // Toggle an option
  const handleToggle = useCallback(
    (option: AccessOption) => {
      const optionValues = Array.isArray(option.value)
        ? option.value
        : [option.value];
      const isSelected = isOptionSelected(option);

      let newValues: string[];
      if (isSelected) {
        // Remove these values
        newValues = currentValue.filter(
          (v: string) => !optionValues.includes(v as AccessStatus),
        );
      } else {
        // Add these values
        newValues = [...new Set([...currentValue, ...optionValues])];
      }

      let newWhere = deleteWhereClause(where, path, true);
      if (newValues.length > 0) {
        newWhere = addWhereClause(newWhere, [...path, '_in'], newValues);
      }
      setFiltersParams(newWhere);
    },
    [
      currentValue,
      isOptionSelected,
      deleteWhereClause,
      where,
      path,
      addWhereClause,
      setFiltersParams,
    ],
  );

  // Clear filter handler
  const handleDelete =
    currentValue.length > 0
      ? () => {
          const newWhere = deleteWhereClause(where, path, true);
          setFiltersParams(newWhere);
        }
      : undefined;

  // Get label for chip based on selected options
  const chipLabel = useMemo(() => {
    if (currentValue.length === 0) {
      return label;
    }
    // Check which options are selected and show first one
    const selectedOption = accessOptions.find(opt => isOptionSelected(opt));
    return selectedOption?.label ?? label;
  }, [currentValue, label, accessOptions, isOptionSelected]);

  return (
    <FilterChip
      label={chipLabel}
      icon={<LockOpenIcon size={16} />}
      onDelete={handleDelete}
      disabled={disabled?.(where) ?? false}
      renderFilter={() => (
        <Paper elevation={3} sx={{ minWidth: 280 }}>
          {accessOptions.map(option => {
            const selected = isOptionSelected(option);
            return (
              <ListItemButton
                key={
                  Array.isArray(option.value)
                    ? option.value.join('-')
                    : option.value
                }
                dense
                onClick={() => handleToggle(option)}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge="start"
                    checked={selected}
                    tabIndex={-1}
                    disableRipple
                    size="small"
                  />
                </ListItemIcon>
                <ListItemIcon sx={{ minWidth: 32 }}>{option.icon}</ListItemIcon>
                <ListItemText
                  primary={option.label}
                  secondary={option.description}
                  slotProps={{
                    primary: { variant: 'body2' },
                    secondary: { variant: 'caption' },
                  }}
                />
              </ListItemButton>
            );
          })}
        </Paper>
      )}
    />
  );
};
